import React, { useMemo } from 'react';
import { ThemeColors } from '../../../../Context/ThemeContext';
import { DateRangePicker } from './DateRangePicker';
import type { CustomDateRange } from '../types';

type ChartKey = 'daily' | 'vsRandom' | 'vsFriends' | 'vsBot' | 'single';

interface ChartDataPoint {
  label: string;
  date?: string;
  // Started counts
  daily: number;
  dailyCompleted?: number;
  dailyFailed?: number;
  vsRandom: number;
  vsFriends: number;
  vsBot: number;
  single: number;
  // Completed counts (solved, not expired)
  vsRandomCompleted?: number;
  vsFriendsCompleted?: number;
  vsBotCompleted?: number;
  singleCompleted?: number;
  // Online users for this period (for completion rate calculation)
  onlineUsers?: number;
}

interface ChartCardProps {
  chartData: ChartDataPoint[];
  chartPeriod: string;
  onPeriodChange: (period: string) => void;
  totalGamesPlayed: number;
  activeLines: Record<ChartKey, boolean>;
  onToggleLine: (key: ChartKey) => void;
  loading: boolean;
  theme: ThemeColors;
  isMobile: boolean;
  customRange?: CustomDateRange;
  onCustomRangeChange?: (range: CustomDateRange | undefined) => void;
}

const lineColors: Record<ChartKey, string> = {
  daily: '#4ade80',
  vsRandom: '#a855f7',
  vsFriends: '#ec4899',
  vsBot: '#facc15',
  single: '#3b82f6',
};

const legendLabels: Record<ChartKey, string> = {
  daily: 'Daily Challenge',
  vsRandom: 'VS Random',
  vsFriends: 'VS Friends',
  vsBot: 'VS Bot',
  single: 'Single Mode',
};

// Helper function to get a "nice" max value for Y-axis
const getNiceMaxValue = (maxValue: number): number => {
  if (maxValue === 0) return 10;
  if (maxValue <= 10) return 10;

  // Find the order of magnitude
  const magnitude = Math.pow(10, Math.floor(Math.log10(maxValue)));

  // Round up to nearest "nice" number
  const normalized = maxValue / magnitude;

  let niceNormalized: number;
  if (normalized <= 1) niceNormalized = 1;
  else if (normalized <= 2) niceNormalized = 2;
  else if (normalized <= 5) niceNormalized = 5;
  else niceNormalized = 10;

  return niceNormalized * magnitude;
};

// Generate nice Y-axis ticks
const getYAxisTicks = (niceMax: number): number[] => {
  const tickCount = 5;
  const step = niceMax / tickCount;
  return Array.from({ length: tickCount + 1 }, (_, i) => Math.round(i * step));
};

export const ChartCard: React.FC<ChartCardProps> = ({
  chartData,
  chartPeriod,
  onPeriodChange,
  totalGamesPlayed,
  activeLines,
  onToggleLine,
  loading,
  theme,
  isMobile,
  customRange,
  onCustomRangeChange,
}) => {


  // Calculate dynamic max value based on active lines only
  const { niceMax, yAxisTicks } = useMemo(() => {
    if (chartData.length === 0) {
      return { niceMax: 100, yAxisTicks: [0, 20, 40, 60, 80, 100] };
    }

    // Get max value only from active lines
    const activeKeys = (Object.keys(activeLines) as ChartKey[]).filter(
      (key) => activeLines[key]
    );

    if (activeKeys.length === 0) {
      return { niceMax: 100, yAxisTicks: [0, 20, 40, 60, 80, 100] };
    }

    const maxValue = Math.max(
      ...chartData.flatMap((d) => activeKeys.map((key) => d[key]))
    );

    const calculatedMax = getNiceMaxValue(maxValue);
    const calculatedTicks = getYAxisTicks(calculatedMax);

    return { niceMax: calculatedMax, yAxisTicks: calculatedTicks };
  }, [chartData, activeLines]);

  const getPath = (data: ChartDataPoint[], key: ChartKey): string => {
    if (data.length === 0) return '';

    const height = 280;
    const width = 1000;
    const paddingLeft = 40;
    const paddingRight = 20;
    const stepX = (width - paddingLeft - paddingRight) / (data.length - 1 || 1);

    return data
      .map((point, i) => {
        const x = paddingLeft + i * stepX;
        const y = height - 40 - (point[key] / niceMax) * (height - 80);
        return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
      })
      .join(' ');
  };

  const styles = {
    chartCard: {
      background: theme.card,
      borderRadius: '8px',
      padding: isMobile ? '12px' : '16px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
      overflowX: 'auto' as const,
    },
    chartHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '12px',
      flexWrap: 'wrap' as const,
      gap: '8px',
    },
    chartTitle: {
      fontSize: '15px',
      fontWeight: 600,
      color: theme.text,
    },
    chartSubtitle: {
      fontSize: '14px',
      color: theme.textSecondary,
      fontWeight: 400,
      marginLeft: '12px',
    },
    periodButtons: {
      display: 'flex',
      gap: '4px',
    },
    periodButton: {
      padding: '4px 12px',
      fontSize: '12px',
      fontWeight: 600,
      border: '1px solid #475569',
      borderRadius: '4px',
      cursor: 'pointer',
      transition: 'all 0.2s',
      background: '#1e293b',
      color: '#94a3b8',
    },
    periodButtonActive: {
      background: '#2563eb',
      color: '#fff',
      border: '1px solid #2563eb',
    },
    legend: {
      display: 'flex',
      justifyContent: 'center',
      gap: isMobile ? '10px' : '20px',
      marginTop: '14px',
      fontSize: '12px',
      flexWrap: 'wrap' as const,
    },
    legendItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      cursor: 'pointer',
      transition: 'all 0.2s',
      userSelect: 'none' as const,
      padding: '4px 8px',
      borderRadius: '4px',
    },
    legendDot: {
      width: '10px',
      height: '10px',
      borderRadius: '50%',
      transition: 'background 0.2s',
    },
    spinner: {
      border: '3px solid rgba(255,255,255,0.3)',
      borderTop: '3px solid #2563eb',
      borderRadius: '50%',
      width: '30px',
      height: '30px',
      animation: 'spin 1s linear infinite',
    },
  };

  return (
    <div style={styles.chartCard}>
      <div style={styles.chartHeader}>
        <div>
          <span style={styles.chartTitle}>Games Played</span>
          <span style={styles.chartSubtitle}>Total: {totalGamesPlayed.toLocaleString()}</span>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <div style={styles.periodButtons}>
            {['7D', '30D', '90D'].map((p) => (
              <button
                key={p}
                style={{
                  ...styles.periodButton,
                  ...(chartPeriod === p ? styles.periodButtonActive : {}),
                }}
                onClick={() => onPeriodChange(p)}
                disabled={loading}
              >
                {p}
              </button>
            ))}
          </div>
          {onCustomRangeChange && (
            <DateRangePicker
              value={customRange}
              onChange={onCustomRangeChange}
              theme={theme}
              style={{ position: 'relative' }}
              isActive={chartPeriod === 'custom'}
            />
          )}
        </div>
      </div>

      {loading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '300px',
          }}
        >
          <div style={styles.spinner} />
        </div>
      ) : chartData.length === 0 ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '300px',
            color: theme.textSecondary,
          }}
        >
          No chart data available
        </div>
      ) : (
        <div style={{ overflowX: 'auto', minWidth: isMobile ? '600px' : 'auto' }}>
          <svg
            width="100%"
            height="300"
            viewBox="0 0 1000 300"
            preserveAspectRatio="xMidYMid meet"
          >
            {/* Dynamic Grid lines */}
            {yAxisTicks.map((val) => {
              const y = 240 - (val / niceMax) * 200;
              return (
                <g key={val}>
                  <line x1="40" y1={y} x2="980" y2={y} stroke={theme.gridLine} strokeWidth="1" />
                  <text x="8" y={y + 4} fontSize="10" fill={theme.textSecondary}>
                    {val.toLocaleString()}
                  </text>
                </g>
              );
            })}

            {/* Lines */}
            {(Object.keys(lineColors) as ChartKey[]).map(
              (key) =>
                activeLines[key] &&
                chartData.length > 0 && (
                  <path
                    key={key}
                    d={getPath(chartData, key)}
                    fill="none"
                    stroke={lineColors[key]}
                    strokeWidth="2.5"
                  />
                )
            )}

            {/* Data points with tooltips */}
            {chartData.map((point, i) => {
              const chartHeight = 280;
              const paddingLeft = 40;
              const paddingRight = 20;
              const stepX = (1000 - paddingLeft - paddingRight) / (chartData.length - 1 || 1);
              const x = paddingLeft + i * stepX;

              return (
                <g key={i}>
                  {(Object.keys(lineColors) as ChartKey[]).map((key) => {
                    if (!activeLines[key]) return null;
                    const y = chartHeight - 40 - (point[key] / niceMax) * (chartHeight - 80);
                    return (
                      <g key={key}>
                        <circle
                          cx={x}
                          cy={y}
                          r="4"
                          fill={lineColors[key]}
                          stroke={theme.card}
                          strokeWidth="2"
                        />
                        <rect
                          x={x - 22}
                          y={y - 22}
                          width="44"
                          height="18"
                          fill={theme.tooltipBg}
                          stroke={theme.tooltipBorder}
                          strokeWidth="1"
                          rx="3"
                        />
                        <text
                          x={x}
                          y={y - 10}
                          fontSize="10"
                          fill={theme.tooltipText}
                          textAnchor="middle"
                          fontWeight="500"
                        >
                          {point[key]}
                        </text>
                      </g>
                    );
                  })}
                </g>
              );
            })}

            {/* X-axis labels */}
            {chartData.map((point, i) => {
              const stepX = (1000 - 60) / (chartData.length - 1 || 1);
              return (
                <text
                  key={i}
                  x={40 + i * stepX}
                  y="275"
                  fontSize="11"
                  fill={theme.textSecondary}
                  textAnchor="middle"
                >
                  {point.label}
                </text>
              );
            })}
          </svg>
        </div>
      )}

      {/* Legend */}
      <div style={styles.legend}>
        {(Object.keys(lineColors) as ChartKey[]).map((key) => (
          <div
            key={key}
            style={{ ...styles.legendItem, opacity: activeLines[key] ? 1 : 0.5 }}
            onClick={() => onToggleLine(key)}
          >
            <div
              style={{
                ...styles.legendDot,
                background: activeLines[key] ? lineColors[key] : '#9ca3af',
              }}
            />
            <span
              style={{
                color: activeLines[key] ? theme.textSecondary : '#9ca3af',
                textDecoration: activeLines[key] ? 'none' : 'line-through',
              }}
            >
              {legendLabels[key]}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export type { ChartDataPoint, ChartKey };